import React, { useEffect, useState } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { BsBell, BsCheckCircle, BsEnvelope } from 'react-icons/bs'
import { FaSms } from 'react-icons/fa'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import api from '../../utils/api'
import { ErrorNotification } from '../../utils'

const NotificationDrawer = ({
  open,
  setOpen,
  notifications,
  mutate
}) => {
  const [drawerWidth, setDrawerWidth] = useState(500)
  const [markingAsRead, setMarkingAsRead] = useState(null)

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 700) {
        setDrawerWidth(window.innerWidth - 20)
      } else {
        setDrawerWidth(500)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize() // set initial size
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
    } else {
      const diffInDays = Math.floor(diffInHours / 24)
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
    }
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'campaign_created':
        return <BsBell className="h-5 w-5 text-blue-500" />
      case 'sms':
        return <FaSms className="h-5 w-5 text-green-500" />
      case 'email':
        return <BsEnvelope className="h-5 w-5 text-purple-500" />
      default:
        return <BsBell className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400'
      case 'pending':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400'
      case 'failed':
        return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400'
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400'
    }
  }

  const getDeliveryMethodIcon = (method) => {
    switch (method) {
      case 'sms':
        return <FaSms className="h-4 w-4 text-green-500" />
      case 'email':
        return <BsEnvelope className="h-4 w-4 text-blue-500" />
      default:
        return <BsBell className="h-4 w-4 text-gray-500" />
    }
  }

  const handleMarkAsRead = async (notificationId, event) => {
    // Prevent event bubbling if clicking on notification
    if (event) {
      event.stopPropagation()
    }

    // Don't mark as read if already read or currently marking
    const notification = notifications?.find(n => n.id === notificationId)
    if (!notification || notification.is_read || markingAsRead === notificationId) {
      return
    }

    try {
      setMarkingAsRead(notificationId)
      await api.patch(`notifications/${notificationId}/read/`)

      // Optimistically update the notification
      if (mutate) {
        // Revalidate the notifications list
        await mutate()
      }
    } catch (error) {
      ErrorNotification(error.response?.data || error.message || 'Failed to mark notification as read')
    } finally {
      setMarkingAsRead(null)
    }
  }

  return (
    <Drawer
      open={open}
      onClose={() => setOpen(false)}
      direction="right"
      size={drawerWidth}
      style={{
        zIndex: 9000,
      }}
    >
      <div className='w-full h-full overflow-y-auto dark:bg-dark'>
        <div className='w-full h-full'>
          <div className='p-4 w-full flex flex-row items-center justify-between border-b border-gray-200 dark:border-border-dark mb-4'>
            <h1 className='text-xl font-bold dark:text-white'>
              Notifications {notifications?.length > 0 ? `(${notifications?.length})` : ''}
            </h1>

            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <AiOutlineCloseCircle className="h-7 w-7 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
            </button>
          </div>

          <div className='px-4 pb-4'>
            {notifications && notifications.length > 0 ? (
              <div className='space-y-3'>
                {notifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    onClick={() => !notification?.is_read && handleMarkAsRead(notification?.id)}
                    className={`p-4 rounded-lg border transition-all dark:bg-dark duration-200 hover:shadow-md ${notification?.is_read
                      ? 'bg-gray-50 border-gray-200 dark:border-gray-700 cursor-default'
                      : 'bg-white border-blue-200 dark:border-border-dark cursor-pointer'
                      } ${markingAsRead === notification?.id ? 'opacity-50' : ''}`}
                  >
                    <div className='flex items-start space-x-3'>
                      <div className='flex-shrink-0 mt-1'>
                        {getNotificationIcon(notification?.notification_type)}
                      </div>

                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center justify-between mb-2'>
                          <h3 className={`text-sm font-semibold ${notification?.is_read
                            ? 'text-gray-700 dark:text-gray-300'
                            : 'text-gray-900 dark:text-white'
                            }`}>
                            {notification?.title}
                          </h3>

                          <div className='flex items-center space-x-2'>
                            {getDeliveryMethodIcon(notification?.delivery_method)}
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(notification?.status)}`}>
                              {notification?.status}
                            </span>
                          </div>
                        </div>

                        <p className={`text-sm mb-2 ${notification?.is_read
                          ? 'text-gray-600 dark:text-gray-400'
                          : 'text-gray-700 dark:text-gray-300'
                          }`}>
                          {notification?.message}
                        </p>

                        {notification?.sender_name && <div className='flex items-center justify-between text-xs text-gray-500 dark:text-gray-400'>
                          <div className='flex items-center space-x-4'>
                            <span>From: {notification?.sender_name}</span>
                            <span>{formatDate(notification?.created_at)}</span>
                          </div>

                          {!notification?.is_read && (
                            <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                          )}
                        </div>}

                        {notification?.sent_at && (
                          <div className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
                            Sent: {formatDate(notification?.sent_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <BsBell className='h-12 w-12 text-gray-400 dark:text-gray-500 mb-4' />
                <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
                  No notifications
                </h3>
                <p className='text-gray-500 dark:text-gray-400'>
                  You're all caught up! No new notifications at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default NotificationDrawer